import { IdName, SelectedItemsObj } from '@components/multi-selector/types';

export interface CustomReportV3BodyParams {
  fromTimestamp: string;
  toTimestamp: string;
  selectedBranchIds?: string[];
  selectedGroups?: SelectedItemsObj;
  allGroupsByBranch?: SelectedItemsObj;
  includeMarkAsPaid: boolean;
  includeQuickCollect: boolean;
  includeTaxInfo?: boolean;
  category?: string;
  groupBy?: string;
  paymentMode?: string;
  customFields?: string[];
  globalFields?: string[];
  memberFields?: string[];
  counsellorNames?: string[] | null;
  reportType: 'branch' | 'organisation';
  // Only rows of members having all of these tags
  tagIds?: number[];
  // Tags to add as Yes/No columns in this order
  columnTagIds?: string[];
}

// An empty group_ids list means "all groups" for the branch
const buildBranchPayload = (
  branchId: string,
  groups: IdName[],
  allGroupsByBranch: SelectedItemsObj
) => {
  if (!groups.length) {
    return { branch_id: branchId, group_ids: [] };
  }

  const allBranchGroups = allGroupsByBranch[branchId] ?? [];
  const isAllGroupsSelected =
    allBranchGroups.length > 0 &&
    groups.length === allBranchGroups.length &&
    allBranchGroups.every((group) =>
      groups.some((selectedGroup) => selectedGroup.id === group.id)
    );

  if (isAllGroupsSelected) {
    return { branch_id: branchId, group_ids: [] };
  }

  return {
    branch_id: branchId,
    group_ids: groups.map((group) => group.id)
  };
};

const buildCounsellorNamesList = (counsellorNames?: string[] | null) =>
  counsellorNames == null
    ? null
    : (counsellorNames.length ? counsellorNames : ['']).map((name) =>
        name.trim()
      );

export const buildCustomReportV3Body = ({
  fromTimestamp,
  toTimestamp,
  selectedBranchIds = [],
  selectedGroups = {},
  allGroupsByBranch = {},
  includeMarkAsPaid,
  includeQuickCollect,
  includeTaxInfo = false,
  category = '',
  groupBy = '',
  paymentMode = '',
  customFields = [],
  globalFields = [],
  memberFields = [],
  counsellorNames,
  reportType,
  tagIds = [],
  columnTagIds = []
}: CustomReportV3BodyParams) => {
  const branches = selectedBranchIds.length
    ? selectedBranchIds.map((branchId) =>
        buildBranchPayload(
          branchId,
          selectedGroups[branchId] ?? [],
          allGroupsByBranch
        )
      )
    : Object.entries(selectedGroups).map(([branchId, groups]) =>
        buildBranchPayload(branchId, groups, allGroupsByBranch)
      );

  return {
    from_timestamp: fromTimestamp,
    to_timestamp: toTimestamp,
    branches,
    include_mark_as_paid: includeMarkAsPaid,
    include_quick_collect: includeQuickCollect,
    include_tax_info: includeTaxInfo,
    category,
    group_by: groupBy,
    payment_mode: paymentMode,
    custom_fields: customFields,
    global_fields: globalFields,
    member_fields: memberFields,
    counsellor_names: buildCounsellorNamesList(counsellorNames),
    report_type: reportType,
    ...(tagIds.length ? { tag_ids: tagIds } : {}),
    ...(columnTagIds.length
      ? {
          column_tag_ids: columnTagIds
            .map(Number)
            .filter((id) => !Number.isNaN(id))
        }
      : {})
  };
};
